import { AuthService } from './../../../base/services/auth.service';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import {
  ClientDTO,
  CreateExportInterfaceConfigurationDTO,
  CsvDataDTO,
  ExportInterfaceConfigurationClient,
  ExportInterfaceConfigurationDetailsDTO,
  ExportInterfaceConfigurationDTO,
  ExportInterfaceConfigurationFilterDTO,
  ExportInterfaceConfigurationMode,
  ExportInterfaceConfigurationType,
  FilterCondition,
  FilterDTO,
  FilterOperator,
  SortDirection,
  UpdateConfigurationFromTemplateDTO,
  UpdateExportInterfaceConfigurationDTO,
} from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { IPagedItems } from 'src/app/base/shared/syncfusion/paged-items.interface';
import { ErrorType } from 'src/app/core/routing/error-types';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';

@Injectable()
export class ConfigurationsService extends ShowInactiveService implements IAdaptorService {

  public filterForm = null;
  public oneTimeFilterConfigId: number = null;

  public importConfigurationId: number = null;

  constructor(
    private exportInterfaceConfigurationClient: ExportInterfaceConfigurationClient,
    private toastService: ToastService,
    private authService: AuthService,
    private spinnerService: SpinnerService) {
    super();
  }

  public selectedClient: ClientDTO;

  public configurationType: ExportInterfaceConfigurationType;

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedItems> {
    const client = this.authService.getUserInfo()?.client;
    if (client) {
      this.selectedClient = client;
    }
    if (!sortBy) {
      sortBy = 'ConfigurationName';
    }

    if (!filters) {
      filters = [];
    }

    if (this.importConfigurationId) {
      filters.push(new FilterDTO({ field: 'ImportConfigurationId', operator: FilterOperator.Equal, value: this.importConfigurationId.toString(), isComplex: false }));
    } else {
      filters.push(new FilterDTO({ field: 'Mode', operator: FilterOperator.Equal, value: ExportInterfaceConfigurationMode[ExportInterfaceConfigurationMode.StandAlone], isComplex: false }));
    }

    if (this.oneTimeFilterConfigId) {
      filters.push(new FilterDTO({ field: 'id', operator: FilterOperator.Equal, value: this.oneTimeFilterConfigId.toString(), isComplex: false}));
      this.oneTimeFilterConfigId = null;
    }

    if (this.filterForm) {
      if (this.filterForm.countryCodes?.length) {
        filters.push(new FilterDTO({
          isComplex: true,
          condition: FilterCondition.Or,
          predicates: this.filterForm.countryCodes.map(country => {
            return new FilterDTO({
              isComplex: false,
              field: 'Client.CountryCode',
              operator: FilterOperator.Equal,
              value: country,
            });
          }),
        }));
      }

      if (this.filterForm.salesPersonCodes?.length) {
        filters.push(new FilterDTO({
          isComplex: true,
          condition: FilterCondition.Or,
          predicates: this.filterForm.salesPersonCodes.map(country => {
            return new FilterDTO({
              isComplex: false,
              field: 'Client.SalesPersonCode',
              operator: FilterOperator.Contains,
              value: country,
            });
          }),
        }));
      }

      if (this.filterForm.searchText) {
        filters.push(new FilterDTO({
          isComplex: false,
          field: 'ConfigurationName',
          operator: FilterOperator.Contains,
          value: this.filterForm.searchText,
        }));
      }

      if (this.filterForm.transferType != null) {
        filters.push(new FilterDTO({
          isComplex: false,
          field: 'TransferType',
          operator: FilterOperator.Equal,
          value: this.filterForm.transferType.toString(),
        }));
      }
    }

    return this.exportInterfaceConfigurationClient.searchExportInterfaceConfigurations(this.selectedClient?.id, skip, take, sortBy, sortDirection, false, filters);
  }

  public addRecord(): Observable<ExportInterfaceConfigurationDTO> {
    let createConfiguration = new CreateExportInterfaceConfigurationDTO({ clientId: this.selectedClient?.id,
                                                                          mode: (this.importConfigurationId ? ExportInterfaceConfigurationMode.Response : ExportInterfaceConfigurationMode.StandAlone),
                                                                          importConfigurationId: this.importConfigurationId });
    this.spinnerService.show();
    return this.exportInterfaceConfigurationClient.createExportInterfaceConfiguration(createConfiguration)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.configurations.messages.create.success-title',
            'i18n.domain.export-configuration.configurations.messages.create.success-message',
          );
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
  }

  public updateRecord(updateConfiguration: UpdateExportInterfaceConfigurationDTO): Observable<ExportInterfaceConfigurationDTO> {
    this.spinnerService.show();
    return this.exportInterfaceConfigurationClient.updateExportInterfaceConfiguration(updateConfiguration)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.configurations.messages.update.success-title',
            'i18n.domain.export-configuration.configurations.messages.update.success-message',
          );
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
  }

  public deleteRecord(id: number) {
    this.spinnerService.show();
    return this.exportInterfaceConfigurationClient.deleteExportInterfaceConfiguration(id)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.configurations.messages.delete.success-title',
            'i18n.domain.export-configuration.configurations.messages.delete.success-message',
          );
        }),
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        }),
      );
  }

  public getConfigurationDetails(id: number): Observable<ExportInterfaceConfigurationDetailsDTO> {
    return this.exportInterfaceConfigurationClient.getExportInterfaceConfigurationDetails(id);
  }

  public executeExportInterfaceConfiguration(configurationId: number): Observable<void> {
    return this.exportInterfaceConfigurationClient.executeExportInterfaceConfiguration(configurationId);
  }

  public updateConfigurationFromTemplate(updateConfiguration: UpdateConfigurationFromTemplateDTO): Observable<ExportInterfaceConfigurationDetailsDTO> {
    this.spinnerService.show();
    return this.exportInterfaceConfigurationClient.updateExportInterfaceConfigurationFromTemplate(updateConfiguration)
      .pipe(
        finalize(() => this.spinnerService.hide()), 
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.export-configuration.configurations.messages.update.success-title',
            'i18n.domain.export-configuration.configurations.messages.update.success-message',
          );
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
  }

  public exportConfigurationsAsCSV(filterDTO: ExportInterfaceConfigurationFilterDTO): Observable<CsvDataDTO> {
    this.spinnerService.show();
    return this.exportInterfaceConfigurationClient.generateExportInterfaceConfigurationListAsCsv(filterDTO)
      .pipe(
        finalize(() => this.spinnerService.hide()),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError, error.response);
          }
          return throwError(() => error);
        }),
      );
  }
}
